import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export const ContractDialog = ({ open, onOpenChange, plan, billing }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setForm({ name: "", company: "", email: "", phone: "" });
    setDone(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!plan) return;
    if (!form.name || !form.company || !form.email || !form.phone) {
      toast.error("Por favor completa todos los campos");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        plan: plan.id,
        billing_cycle: billing,
      });
      setDone(true);
      toast.success("¡Solicitud enviada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) setTimeout(reset, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none max-w-lg" data-testid="contract-dialog">
        {done ? (
          <div className="py-8 text-center">
            <CheckCircle size={56} weight="fill" className="mx-auto text-[#2A9D8F] mb-4" />
            <h3 className="font-heading font-black text-2xl tracking-tight mb-2">¡Listo!</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Recibimos tu solicitud para el <strong>Plan {plan?.name}</strong>. Te enviamos un correo de confirmación
              y un asesor te contactará en las próximas <strong>24 horas hábiles</strong>.
            </p>
            <Button
              onClick={() => handleClose(false)}
              className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#001D66]"
              data-testid="contract-done-btn"
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-black text-xl">
                Contratar Plan {plan?.name}
              </DialogTitle>
              <DialogDescription>
                {billing === "annual" ? "Pago anual" : "Pago mensual"} ·{" "}
                <span className="text-[#002FA7] font-bold">
                  Q
                  {(billing === "annual" ? plan?.annual_equivalent_monthly : plan?.price_monthly)?.toLocaleString()}
                  /mes
                </span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-mono uppercase">Nombre completo</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-none mt-1"
                  required
                  data-testid="lead-name"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Empresa</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="rounded-none mt-1"
                  required
                  data-testid="lead-company"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Correo electrónico</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none mt-1"
                    required
                    data-testid="lead-email"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Teléfono</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+502 5555-1234"
                    className="rounded-none mt-1"
                    required
                    data-testid="lead-phone"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Al enviar este formulario aceptas que un asesor de LicitaAI te contacte por correo o teléfono.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  className="rounded-none"
                  data-testid="lead-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="rounded-none bg-[#002FA7] hover:bg-[#001D66]"
                  data-testid="lead-submit"
                >
                  {loading ? "Enviando..." : "Enviar solicitud"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
